import { jsx, jsxs } from "react/jsx-runtime";
import React from "react";
import { InfoHelper, showConfirmationDialog, wrapDialog } from "@teselagen/ui";
import { compose } from "redux";
import {
  Classes,
  ButtonGroup,
  InputGroup,
  Popover,
  HTMLSelect,
  Tooltip,
  AnchorButton,
  Intent,
  Button
} from "@blueprintjs/core";
import classNames from "classnames";
import withEditorProps from "../../withEditorProps";
import EnzymeViewer from "../../EnzymeViewer";
import "./style.css";
import { map, flatMap, countBy, reduce, uniq, omitBy } from "lodash-es";
import {
  aliasedEnzymesByName,
  defaultEnzymesByName,
  getCutsitesFromSequence
} from "@teselagen/sequence-utils";
import { store, view } from "@risingstack/react-easy-state";
import { showDialog } from "../../GlobalDialogUtils";
import builtinEnzymeGroups from "../../constants/builtinEnzymeGroups";
const upsertLocalEnzymeGroups = (newGroups) => {
  const existingGroups = window.getExistingEnzymeGroups();
  const toUpsert = omitBy(
    //delete any groups that have a value of null/undefined
    {
      ...existingGroups,
      ...newGroups
    },
    (val) => {
      return !val;
    }
  );
  localStorage.setItem("enzymeGroups", JSON.stringify(toUpsert));
};
window.createNewEnzymeGroup = window.createNewEnzymeGroup || ((newName) => {
  return upsertLocalEnzymeGroups({
    [newName]: []
  });
});
window.editEnzymeGroupName = window.editEnzymeGroupName || ((oldName, newName) => {
  upsertLocalEnzymeGroups({
    [newName]: window.getExistingEnzymeGroups()[oldName]
  });
  window.deleteEnzymeGroup(oldName);
});
window.deleteEnzymeGroup = window.deleteEnzymeGroup || ((nameToDelete) => {
  return upsertLocalEnzymeGroups({
    [nameToDelete]: null
  });
});
window.updateEnzymeGroup = window.updateEnzymeGroup || ((groupName, enzymes) => {
  return upsertLocalEnzymeGroups({
    [groupName]: enzymes
  });
});
window.getExistingEnzymeGroups = window.getExistingEnzymeGroups || (() => {
  const existingGroups = JSON.parse(
    localStorage.getItem("enzymeGroups") || "{}"
  );
  const toRet = {
    ...builtinEnzymeGroups,
    ...existingGroups
  };
  if (!Object.keys(toRet || {}).length) {
    return {};
  }
  map(toRet, (g, k) => {
    if (!g || !g.length) toRet[k] = [];
  });
  return toRet;
});
const easyStore = store({ hoveredEnzyme: "" });
class EnzymesDialog extends React.Component {
  state = {
    selectedEnzymeGroup: "My Enzymes",
    searchInput: ""
  };
  componentDidMount() {
    this.refreshEnzymeGroups();
  }
  getAllEnzymesByName = () => {
    return {
      ...this.props.additionalEnzymes,
      ...aliasedEnzymesByName
    };
  };
  refreshEnzymeGroups = () => {
    const { sequenceData, additionalEnzymes } = this.props;
    const existingGroups = window.getExistingEnzymeGroups();
    const orderedExistingGroups = {};
    const groupedEnzymesNameMap = {};
    Object.keys(existingGroups).sort().forEach(function(key) {
      const group = existingGroups[key];
      orderedExistingGroups[key] = group;
      group.forEach((name) => {
        const lowerName = name.toLowerCase();
        groupedEnzymesNameMap[lowerName] = aliasedEnzymesByName[lowerName] || additionalEnzymes[lowerName];
      });
    });
    const myEnzymes = {
      ...groupedEnzymesNameMap,
      ...additionalEnzymes,
      ...defaultEnzymesByName
    };
    const hiddenEnzymes = omitBy(aliasedEnzymesByName, (p, i) => {
      return myEnzymes[i] || groupedEnzymesNameMap[i];
    });
    this.enzymeGroups = {
      "My Enzymes": {
        protected: true,
        name: "My Enzymes",
        enzymes: map(myEnzymes).sort((e) => e.name)
      },
      Hidden: {
        protected: true,
        name: "Hidden",
        tooltipInfo: "These less common enzymes are hidden by default. Adding them to a custom group will make them show up in the cutsite filter dropdown.",
        enzymes: map(hiddenEnzymes).sort((e) => e.name)
      },
      ...reduce(
        orderedExistingGroups,
        (acc, val, key) => {
          acc[key] = {
            name: key,
            protected: !!builtinEnzymeGroups[key],
            enzymes: getEnzymesForNames(val, this.getAllEnzymesByName())
          };
          return acc;
        },
        {}
      )
      // "my special enzymes": {
      //   name: "my special enzymes",
      //   enzymes: getEnzymesForNames(["bamhi", "bsmbi"])
      // },
      // "my casual enzymes": {
      //   name: "my casual enzymes",
      //   enzymes: getEnzymesForNames(["bamhi", "bsmbi"])
      // }
    };
    this.cutsitesByEnzymeName = getCutsitesFromSequence(
      sequenceData.sequence,
      sequenceData.circular,
      map(this.getAllEnzymesByName())
    );
    this.forceUpdate();
  };
  clearSelection = () => {
    const { selectedEnzymeGroup } = this.state;
    const selectedEnzymesKey = `selectedEnzymes_${selectedEnzymeGroup}`;
    this.setState({
      [selectedEnzymesKey]: {}
    });
  };
  render() {
    const { hideModal } = this.props;
    if (!this.enzymeGroups) return null;
    const { selectedEnzymeGroup, searchInput, enzymeGroupToMoveTo } = this.state;
    const selectedEnzymesKey = `selectedEnzymes_${selectedEnzymeGroup}`;
    const selectedEnzymesForGroup = this.state[selectedEnzymesKey] || {};
    const selectedCount = countBy(selectedEnzymesForGroup, (e) => e).true || 0;
    const fullSelectedEnzGroup = this.enzymeGroups[selectedEnzymeGroup] || {};
    const enzymeList = flatMap(fullSelectedEnzGroup.enzymes, (enz, i) => {
      const name = `${enz.name} (${this.cutsitesByEnzymeName && this.cutsitesByEnzymeName[enz.name] && this.cutsitesByEnzymeName[enz.name].length || 0})`;
      if (searchInput && !name.toLowerCase().includes(searchInput.toLowerCase())) {
        return [];
      }
      return /* @__PURE__ */ jsx(
        "div",
        {
          onMouseOver: () => {
            easyStore.hoveredEnzyme = enz.name;
            clearTimeout(this.clearId);
          },
          onMouseLeave: () => {
            this.clearId = setTimeout(() => {
              easyStore.hoveredEnzyme = "";
            }, 100);
          },
          onClick: () => {
            this.setState({
              [selectedEnzymesKey]: {
                ...selectedEnzymesForGroup,
                [enz.name]: !selectedEnzymesForGroup[enz.name]
              }
            });
          },
          className: classNames(Classes.MENU_ITEM, "veEnzymeDialogEnzyme", {
            [`${Classes.ACTIVE} ${Classes.INTENT_PRIMARY}`]: selectedEnzymesForGroup[enz.name]
          }),
          children: name
        },
        i
      );
    });
    return /* @__PURE__ */ jsx(
      "div",
      {
        style: { height: 500 },
        className: classNames(
          Classes.DIALOG_BODY,
          "tg-min-width-dialog veEnzymeDialog"
        ),
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            className: "veEnzymeDialogInner",
            style: { display: "flex", height: "100%" },
            children: [
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "veEnzymeDialogEnzymeGroups",
                  style: {
                    minWidth: 150,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRight: "1px solid lightgrey",
                    paddingRight: 5
                  },
                  children: [
                    /* @__PURE__ */ jsx("h5", { children: "Enzyme Groups:" }),
                    /* @__PURE__ */ jsx("div", { style: { overflowY: "auto" }, children: map(this.enzymeGroups, (group, i) => {
                      return /* @__PURE__ */ jsxs(React.Fragment, { children: [
                        /* @__PURE__ */ jsxs(
                          "div",
                          {
                            onClick: () => {
                              this.setState({
                                selectedEnzymeGroup: group.name
                              });
                            },
                            className: classNames(Classes.MENU_ITEM, {
                              [Classes.ACTIVE]: selectedEnzymeGroup === group.name
                            }),
                            style: { marginBottom: 5, cursor: "pointer" },
                            children: [
                              "\xA0 ",
                              group.name,
                              " \xA0",
                              " ",
                              group.tooltipInfo && /* @__PURE__ */ jsx(
                                InfoHelper,
                                {
                                  color: "grey",
                                  isInline: true,
                                  content: group.tooltipInfo
                                }
                              )
                            ]
                          },
                          i
                        ),
                        group.name === "Hidden" && /* @__PURE__ */ jsx("div", { className: Classes.MENU_DIVIDER })
                      ] });
                    }) }),
                    /* @__PURE__ */ jsxs(ButtonGroup, { style: { width: "fit-content" }, children: [
                      /* @__PURE__ */ jsx(
                        Tooltip,
                        {
                          disabled: window.Cypress,
                          position: "bottom",
                          content: "Create New Group",
                          children: /* @__PURE__ */ jsx(
                            Popover,
                            {
                              content: /* @__PURE__ */ jsxs(
                                "div",
                                {
                                  className: "veNewEnzymeGroupPopover",
                                  style: { padding: 10 },
                                  children: [
                                    /* @__PURE__ */ jsx("h5", { children: "Create new enzyme group" }),
                                    /* @__PURE__ */ jsxs("div", { style: { display: "flex" }, children: [
                                      /* @__PURE__ */ jsx(
                                        InputGroup,
                                        {
                                          autoFocus: true,
                                          className: "veNewEnzymeGroupName"
                                        }
                                      ),
                                      /* @__PURE__ */ jsx(
                                        AnchorButton,
                                        {
                                          className: Classes.POPOVER_DISMISS,
                                          onClick: () => {
                                            const newName = document.querySelector(
                                              ".veNewEnzymeGroupName input"
                                            ).value;
                                            if (!newName) {
                                              return window.toastr.error("Invalid name");
                                            }
                                            if (this.enzymeGroups[newName]) {
                                              window.toastr.error(
                                                "Choose a different group name"
                                              );
                                            } else {
                                              window.toastr.success(
                                                "Created New Enzyme Group " + newName
                                              );
                                              window.createNewEnzymeGroup(newName);
                                              this.refreshEnzymeGroups();
                                              this.setState({
                                                selectedEnzymeGroup: newName
                                              });
                                            }
                                          },
                                          icon: "tick",
                                          intent: "success"
                                        }
                                      )
                                    ] })
                                  ]
                                }
                              ),
                              children: /* @__PURE__ */ jsx(
                                AnchorButton,
                                {
                                  className: "veEnzymeDialogAddGroupBtn",
                                  minimal: true,
                                  icon: "add"
                                }
                              )
                            }
                          )
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Popover,
                        {
                          content: /* @__PURE__ */ jsxs(
                            "div",
                            {
                              className: "veEditEnzymeGroupPopover",
                              style: { padding: 10 },
                              children: [
                                /* @__PURE__ */ jsx("h5", { children: "Edit group name" }),
                                /* @__PURE__ */ jsxs("div", { style: { display: "flex" }, children: [
                                  /* @__PURE__ */ jsx(
                                    InputGroup,
                                    {
                                      defaultValue: selectedEnzymeGroup,
                                      autoFocus: true,
                                      className: "veEditEnzymeGroupName"
                                    }
                                  ),
                                  /* @__PURE__ */ jsx(
                                    AnchorButton,
                                    {
                                      className: Classes.POPOVER_DISMISS,
                                      onClick: () => {
                                        const newName = document.querySelector(
                                          ".veEditEnzymeGroupName input"
                                        ).value;
                                        if (!newName) {
                                          return window.toastr.error("Invalid name");
                                        }
                                        if (this.enzymeGroups[newName]) {
                                          window.toastr.error(
                                            "Choose a different group name"
                                          );
                                        } else {
                                          window.toastr.success("Edit Successful");
                                          window.editEnzymeGroupName(
                                            selectedEnzymeGroup,
                                            newName
                                          );
                                          this.refreshEnzymeGroups();
                                          this.setState({
                                            selectedEnzymeGroup: newName
                                          });
                                        }
                                      },
                                      icon: "tick",
                                      intent: "success"
                                    }
                                  )
                                ] })
                              ]
                            }
                          ),
                          children: /* @__PURE__ */ jsx(
                            Tooltip,
                            {
                              disabled: window.Cypress,
                              position: "bottom",
                              content: "Edit Group Name",
                              children: /* @__PURE__ */ jsx(
                                AnchorButton,
                                {
                                  disabled: fullSelectedEnzGroup.protected,
                                  minimal: true,
                                  icon: "edit"
                                }
                              )
                            }
                          )
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Tooltip,
                        {
                          disabled: window.Cypress,
                          position: "bottom",
                          content: "Delete Group",
                          children: /* @__PURE__ */ jsx(
                            AnchorButton,
                            {
                              disabled: fullSelectedEnzGroup.protected,
                              onClick: async () => {
                                const confirm = await showConfirmationDialog({
                                  text: `Are you sure you want to delete this group, ${selectedEnzymeGroup}?`,
                                  intent: Intent.DANGER
                                });
                                if (confirm) {
                                  window.deleteEnzymeGroup(selectedEnzymeGroup);
                                  this.refreshEnzymeGroups();
                                  this.setState({
                                    selectedEnzymeGroup: "My Enzymes"
                                  });
                                }
                              },
                              minimal: true,
                              icon: "trash"
                            }
                          )
                        }
                      )
                    ] })
                  ]
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: "veEnzymeDialogEnzymes", children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: "veEnzymeDialogSearch",
                    style: { width: 250, paddingBottom: 5 },
                    children: /* @__PURE__ */ jsx(
                      InputGroup,
                      {
                        round: true,
                        placeholder: "Search by name or # of cuts",
                        onChange: (e) => {
                          this.setState({
                            searchInput: e.target.value
                          });
                        },
                        value: searchInput,
                        rightElement: searchInput && /* @__PURE__ */ jsx(
                          AnchorButton,
                          {
                            icon: "cross",
                            minimal: true,
                            intent: "danger",
                            onClick: (e) => {
                              this.setState({ searchInput: "" });
                              e.stopPropagation();
                            }
                          }
                        ),
                        leftIcon: "search"
                      }
                    )
                  }
                ),
                /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      height: "84%",
                      display: "flex",
                      flexFlow: "column",
                      justifyContent: "space-between"
                    },
                    children: [
                      /* @__PURE__ */ jsx(
                        "div",
                        {
                          className: "veEnzymeDialogEnzymesList",
                          style: {
                            height: "fit-parent",
                            overflowY: "auto",
                            display: "flex",
                            flexWrap: "wrap",
                            maxWidth: "100%"
                          },
                          children: enzymeList.length ? enzymeList : "Copy enzymes from other groups to your custom group"
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        HoverView,
                        {
                          allEnzymesByName: this.getAllEnzymesByName()
                        }
                      )
                    ]
                  }
                ),
                /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between" }, children: [
                  /* @__PURE__ */ jsxs(ButtonGroup, { children: [
                    /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
                      Tooltip,
                      {
                        position: "left",
                        content: "Copy Selection To Another Group",
                        children: /* @__PURE__ */ jsx(
                          Popover,
                          {
                            canEscapeKeyClose: true,
                            disabled: !selectedCount,
                            position: "top",
                            content: /* @__PURE__ */ jsx(
                              MoveToInner,
                              {
                                ...{
                                  clearSelection: this.clearSelection,
                                  refreshEnzymeGroups: this.refreshEnzymeGroups,
                                  selectedEnzymeGroup,
                                  enzymeGroupToMoveTo,
                                  selectedCount,
                                  selectedEnzymesForGroup,
                                  enzymeGroups: this.enzymeGroups,
                                  setStateAbove: (val) => {
                                    this.setState(val);
                                  }
                                }
                              }
                            ),
                            target: /* @__PURE__ */ jsx(
                              AnchorButton,
                              {
                                className: "veEnzymeGroupAddEnzymesBtn",
                                disabled: !selectedCount,
                                icon: "duplicate"
                              }
                            )
                          }
                        )
                      }
                    ) }),
                    /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
                      Tooltip,
                      {
                        disabled: window.Cypress,
                        position: "bottom",
                        content: "Remove Enzymes",
                        children: /* @__PURE__ */ jsx(
                          AnchorButton,
                          {
                            className: "veRemoveEnzymeFromGroupBtn",
                            onClick: async () => {
                              const confirm = await showConfirmationDialog({
                                text: `Are you sure you want to remove ${selectedCount} enzyme(s) from ${selectedEnzymeGroup}`,
                                intent: Intent.DANGER
                              });
                              if (confirm) {
                                const enzymes = flatMap(
                                  fullSelectedEnzGroup.enzymes,
                                  (e) => selectedEnzymesForGroup[e.name] ? [] : e.name
                                );
                                window.updateEnzymeGroup(
                                  selectedEnzymeGroup,
                                  enzymes
                                );
                                this.setState({ [selectedEnzymesKey]: {} });
                                this.refreshEnzymeGroups();
                                window.toastr.success(
                                  `${selectedCount} enzyme(s) removed`
                                );
                              }
                            },
                            disabled: fullSelectedEnzGroup.protected || !selectedCount,
                            icon: "trash"
                          }
                        )
                      }
                    ) }),
                    /* @__PURE__ */ jsx("div", {}),
                    /* @__PURE__ */ jsxs(
                      AnchorButton,
                      {
                        onClick: () => {
                          this.setState({ [selectedEnzymesKey]: {} });
                        },
                        disabled: !selectedCount,
                        children: [
                          "Deselect ",
                          selectedCount
                        ]
                      }
                    )
                  ] }),
                  selectedEnzymeGroup === "My Enzymes" && /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
                    Tooltip,
                    {
                      disabled: window.Cypress,
                      position: "left",
                      content: "Create Custom Enzyme",
                      children: /* @__PURE__ */ jsx(
                        Button,
                        {
                          minimal: true,
                          onClick: () => {
                            hideModal();
                            showDialog({
                              dialogType: "CreateCustomEnzyme"
                            });
                          },
                          className: "veEnzymeCreateCustomEnzyme",
                          icon: "add"
                        }
                      )
                    }
                  ) })
                ] })
              ] })
            ]
          }
        )
      }
    );
  }
}
export default compose(
  wrapDialog({ title: "Manage Enzymes" }),
  withEditorProps
  // reduxForm({
  //   form: "EnzymesDialog",
  //   validate: ({ id1, id2 }) => {
  //     const errors = {};
  //     if (!id1 || Array.isArray(id1)) {
  //       errors.id1 = "Please select a feature";
  //     }
  //     if (!id2 || Array.isArray(id2)) {
  //       errors.id2 = "Please select a feature";
  //     }
  //     return errors;
  //   }
  // }),
  // formValues("id1", "id2")
)(EnzymesDialog);
function getEnzymesForNames(names, allEnzymesByName) {
  return names.map((n) => {
    return n && allEnzymesByName[n.toLowerCase()] || { name: "Not Found!" };
  });
}
class MoveToInner extends React.Component {
  componentDidMount() {
    const {
      selectedEnzymeGroup,
      enzymeGroupToMoveTo,
      setStateAbove,
      enzymeGroups
    } = this.props;
    const enzymeOpts = flatMap(enzymeGroups, (g) => {
      if (g.protected || g.name === selectedEnzymeGroup) return [];
      return {
        value: g.name
      };
    });
    if (!enzymeGroupToMoveTo && enzymeOpts.length) {
      setStateAbove({ enzymeGroupToMoveTo: enzymeOpts[0].value });
    }
    this.setState({
      enzymeOpts
    });
  }
  state = { enzymeOpts: [] };
  render() {
    const {
      clearSelection,
      setStateAbove,
      selectedCount,
      enzymeGroupToMoveTo,
      enzymeGroups,
      refreshEnzymeGroups,
      selectedEnzymesForGroup
    } = this.props;
    const { enzymeOpts } = this.state;
    return /* @__PURE__ */ jsx("div", { className: "veEnzymeGroupMoveEnzymePopover", style: { padding: 10 }, children: enzymeOpts && !enzymeOpts.length ? /* @__PURE__ */ jsx("div", { style: { maxWidth: 150 }, children: "No other custom enzyme groups exist. Please create a new enzyme group to add enzymes to it." }) : /* @__PURE__ */ jsxs("div", { children: [
      /* @__PURE__ */ jsxs("h5", { children: [
        "Copy ",
        selectedCount,
        " Enzyme(s) To:"
      ] }),
      /* @__PURE__ */ jsx(
        HTMLSelect,
        {
          onChange: (e) => {
            setStateAbove({
              enzymeGroupToMoveTo: e.target.value
            });
          },
          value: enzymeGroupToMoveTo,
          options: enzymeOpts
        }
      ),
      /* @__PURE__ */ jsx(
        AnchorButton,
        {
          className: Classes.POPOVER_DISMISS,
          onClick: () => {
            const enzymes = uniq([
              ...map(
                enzymeGroups[enzymeGroupToMoveTo].enzymes,
                (e) => e.name
              ),
              ...flatMap(
                selectedEnzymesForGroup,
                (selected, name) => selected ? name : []
              )
            ]);
            window.updateEnzymeGroup(enzymeGroupToMoveTo, enzymes);
            refreshEnzymeGroups();
            clearSelection();
            window.toastr.success(
              `${selectedCount} enzyme(s) moved to ${enzymeGroupToMoveTo}`
            );
          },
          intent: "success",
          icon: "tick"
        }
      )
    ] }) });
  }
}
const HoverView = view(({ allEnzymesByName }) => {
  const { hoveredEnzyme } = easyStore;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: "hoveredEnzymeContainer",
      style: {
        overflow: "hidden",
        // maxWidth: "100%",
        minWidth: 0,
        // width: "100%",
        margin: "20px",
        minHeight: 30,
        maxHeight: 30,
        display: "flex",
        justifyContent: "space-between"
      },
      children: [
        hoveredEnzyme,
        " ",
        hoveredEnzyme && /* @__PURE__ */ jsx(
          EnzymeViewer,
          {
            ...{
              paddingEnd: "--",
              paddingStart: "--",
              sequence: allEnzymesByName[hoveredEnzyme.toLowerCase()].site,
              reverseSnipPosition: allEnzymesByName[hoveredEnzyme.toLowerCase()].bottomSnipOffset,
              forwardSnipPosition: allEnzymesByName[hoveredEnzyme.toLowerCase()].topSnipOffset
            }
          }
        )
      ]
    }
  );
});
